import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { CustomerHistoryDocDto, CustomerHistoryDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { amt } from "../ledger/common";

// A customer's history (acceptance pass, D-3 and D-12, rule W-12), on the customer page and in the register: Delta's
// own posted sales and refunds a page at a time (drafts apart), totals over every document in the user's scope, how
// the ledger balance is made up, and the legacy documents the migration keeps for the customer, as stored. Amounts
// keep the stored third decimal and mark it. `external`: links open in a new tab (the register has its own window).

/** True when the amount's third decimal is used (imported history keeps three). */
const residual = (x: string) => /\.\d\d[1-9]/.test(x);

export function CustomerHistory({ partyId, external }: { partyId: string; external?: boolean }) {
  const { t } = useLang();
  const { missingFor } = useSession();
  const [h, setH] = useState<CustomerHistoryDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [pages, setPages] = useState({ page: 1, draftPage: 1, legacyPage: 1 });
  useEffect(() => {
    let live = true;
    setFailed(null);
    api<CustomerHistoryDto>("GET", `/customers/${partyId}/history?page=${pages.page}&draftPage=${pages.draftPage}&legacyPage=${pages.legacyPage}`)
      .then((x) => { if (live) setH(x); }, (e) => { if (live) setFailed(errorText(t, e)); });
    return () => { live = false; };
  }, [partyId, t, pages]);
  useEffect(() => { setH(null); setPages({ page: 1, draftPage: 1, legacyPage: 1 }); }, [partyId]);
  if (!h) return <div className={`state-box ${failed ? "error" : ""}`}>{failed ? <p role="alert">{failed}</p> : <p>{t("loading")}</p>}</div>;
  const tgt = external ? { target: "_blank", rel: "noopener" } : {};
  const canDoc = !missingFor("/invoices/show/x").length, canStatement = !missingFor("/reports/account-statement").length;
  const statement = `/reports/account-statement?partyId=${h.party.id}`;
  const when = (x: string | null) => (x ? x.replace("T", " ") : "–");
  const amounts = (o: unknown): string[] => (typeof o === "string" ? [o] : o && typeof o === "object" ? Object.values(o).flatMap(amounts) : []);
  const anyResidual = amounts([h.ledger, h.totals, h.reconciliation, h.native.rows, h.drafts.rows]).some((x) => /^-?\d+\.\d{3}$/.test(x) && residual(x));
  const M = ({ v, id, neg }: { v: string; id?: string; neg?: boolean }) => {
    const r = residual(v);
    return <span className={`num ${r ? "ch-res" : ""}`} title={r ? t("chResidual") : undefined} data-testid={id} data-residual={r || undefined}>{neg && /[1-9]/.test(v) ? "-" : ""}{amt(v)}{r ? " ◦" : ""}</span>;
  };
  const Pager = ({ p, k, id }: { p: { total: number; page: number; pageSize: number }; k: keyof typeof pages; id: string }) => {
    if (p.total <= p.pageSize) return null;
    const last = Math.ceil(p.total / p.pageSize);
    return (
      <div className="ch-pager" data-testid={id}>
        <button type="button" className="btn btn-sm" disabled={p.page <= 1} onClick={() => setPages((x) => ({ ...x, [k]: p.page - 1 }))}>{t("chPrev")}</button>
        <span className="num">{t("chShowing", { a: (p.page - 1) * p.pageSize + 1, b: Math.min(p.page * p.pageSize, p.total), n: p.total })}</span>
        <button type="button" className="btn btn-sm" disabled={p.page >= last} onClick={() => setPages((x) => ({ ...x, [k]: p.page + 1 }))}>{t("chNext")}</button>
      </div>
    );
  };
  const state = (i: CustomerHistoryDocDto) => (i.status === "draft" ? t("chDraft") : i.reversed ? t("chReversed") : t("chPosted"));
  const docTable = (rows: CustomerHistoryDocDto[], id: string) => (
    <div className="table-wrap"><table className="tbl" data-testid={id}>
      <thead><tr><th>{t("chDoc")}</th><th>{t("date")}</th><th>{t("status")}</th><th className="end">{t("total")}</th><th className="end">{t("chPaidAtSale")}</th><th className="end">{t("chUnpaid")}</th>
        <th className="end">{t("chRefunded")}</th><th className="end">{t("chRefundCredit")}</th><th className="end">{t("chAllocated")}</th><th className="end">{t("chOpen")}</th></tr></thead>
      <tbody>{rows.length ? rows.map((i) => {
        const sale = i.kind === "sale";
        return (
          <tr key={i.id} className={i.reversed ? "muted" : ""} data-testid="ch-row">
            <td>{canDoc ? <Link className="num" to={`/invoices/show/${i.id}`} {...tgt}>{i.code ?? t("journalDraft")}</Link> : <span className="num">{i.code ?? t("journalDraft")}</span>}
              <small className="ch-kind"> {sale ? t("chSale") : t("chRefund")}{i.pos ? ` · ${t("chPos")}` : ""}{i.refundOf ? ` · ${t("chRefundOf", { x: i.refundOf.code })}` : ""}</small></td>
            <td className="num">{when(i.invoiceAt)}</td><td>{state(i)}</td>
            <td className="end"><M v={i.grand} neg={!sale} /></td><td className="end"><M v={i.paid} /></td>
            <td className="end">{sale ? <M v={i.unpaid} /> : <M v={i.unpaid} neg />}</td>
            <td className="end">{sale ? <M v={i.refunded} /> : "–"}</td><td className="end">{sale ? <M v={i.refundCredited} /> : "–"}</td>
            <td className="end">{sale ? <M v={i.allocated} /> : "–"}</td>
            <td className="end">{sale ? <><M v={i.open} />{/[1-9]/.test(i.excessCredit) && <small className="ch-kind"> · {t("chExcess")} <M v={i.excessCredit} /></small>}</> : "–"}</td></tr>);
      }) : <tr><td colSpan={10} className="muted">{t("chNoNative")}</td></tr>}</tbody>
    </table></div>
  );
  const s = h.totals.sales, rc = h.reconciliation;
  return (
    <div className="ch" data-testid="customer-history">
      <div className="ch-tiles">
        <div><span>{t("chBalance")}</span><b><M v={h.ledger.balance} id="ch-balance" /></b>
          {/[1-9]/.test(h.ledger.migrated) && <small className="ch-kind" data-testid="ch-migrated">{t("chMigratedPart", { x: amt(h.ledger.migrated) })}</small>}</div>
        <div><span>{t("chSales")}</span><b><M v={s.total} id="ch-sales" /></b></div>
        <div><span>{t("chUnpaid")}</span><b><M v={s.unpaidAtSale} id="ch-unpaid" /></b></div>
        <div><span>{t("chRefundCredit")}</span><b><M v={s.refundCredited} id="ch-credit" /></b></div>
        <div><span>{t("chAllocated")}</span><b><M v={s.allocated} id="ch-allocated" /></b></div>
        <div><span>{t("chOpen")}</span><b><M v={s.open} id="ch-open" /></b></div>
      </div>
      <p className="hint">{t("chBalanceHint")} {t("chW12")}</p>
      {canStatement && <Link className="btn btn-sm" to={statement} {...tgt} data-testid="ch-statement"><Icon name="file" />{t("chStatement")}</Link>}

      <h3 className="ch-h">{t("chReconTitle")}</h3>
      {rc ? (
        <div className="ch-recon" data-testid="ch-recon">
          <table className="tbl"><tbody>
            <tr><td>{t("chReconOpen")}</td><td className="end"><M v={rc.openOnInvoices} /></td></tr>
            {rc.bySource.filter((x) => ["opening_balance", "manual", "migrated", "other"].includes(x.source)).map((x) =>
              <tr key={x.source}><td>+ {t(`chSrc_${x.source}`)}</td><td className="end"><M v={x.amount} /></td></tr>)}
            <tr><td>− {t("chReconUnallocated")}</td><td className="end"><M v={rc.unallocatedCollections} /></td></tr>
            <tr className="strong"><td>= {t("chReconComputed")}</td><td className="end"><M v={rc.computed} id="ch-computed" /></td></tr>
            <tr><td>{t("chReconBalance")}</td><td className="end"><M v={h.ledger.balance} /></td></tr>
            <tr><td>{t("chReconDiff")}</td><td className="end"><span className={`pill ${/[1-9]/.test(rc.difference) ? "bad" : "good"}`} data-testid="ch-diff">{amt(rc.difference)}</span></td></tr>
          </tbody></table>
          <table className="tbl"><thead><tr><th>{t("chReconBySource")}</th><th className="end">{t("chAmount")}</th></tr></thead><tbody>
            {rc.bySource.map((x) => <tr key={x.source}><td>{t(`chSrc_${x.source}`)}</td><td className="end"><M v={x.amount} /></td></tr>)}
          </tbody></table>
          {/[1-9]/.test(rc.allocatedToOpenItems) && <p className="hint">{t("chReconOpenItems", { x: amt(rc.allocatedToOpenItems) })}</p>}
        </div>
      ) : <p className="hint" data-testid="ch-recon-partial">{t("chReconPartial")}</p>}

      <h3 className="ch-h"><span className="pill good">{t("chNative")}</span> {t("chNativeTitle")} <span className="count num" data-testid="ch-native-count">{h.native.total}</span></h3>
      {h.hiddenByScope.native > 0 && <p className="hint" data-testid="ch-hidden">{t("chHidden", { n: h.hiddenByScope.native })}</p>}
      {docTable(h.native.rows, "ch-native")}
      <Pager p={h.native} k="page" id="ch-native-pager" />
      {h.drafts.total > 0 && <>
        <h3 className="ch-h">{t("chDrafts")} <span className="count num" data-testid="ch-drafts-count">{h.drafts.total}</span></h3>
        {docTable(h.drafts.rows, "ch-drafts")}
        <Pager p={h.drafts} k="draftPage" id="ch-drafts-pager" />
      </>}

      <h3 className="ch-h"><span className="pill">{t("chLegacy")}</span> {t("chLegacyTitle")} <span className="count num">{h.legacy.total}</span></h3>
      {h.hiddenByScope.legacy > 0 && <p className="hint" data-testid="ch-hidden-legacy">{t("chHiddenLegacy", { n: h.hiddenByScope.legacy })}</p>}
      {h.legacyInOrganization === 0 ? <p className="hint" data-testid="ch-no-migration">{t("chNoMigration")}</p>
        : !h.legacy.total ? <p className="hint" data-testid="ch-no-legacy">{t("chNoLegacy")}</p>
          : <><p className="hint">{t("chLegacyHint")}</p>
            <div className="table-wrap"><table className="tbl" data-testid="ch-legacy">
              <thead><tr><th>{t("chLegacyNo")}</th><th>{t("date")}</th><th>{t("chLegacyKind")}</th><th>{t("chLegacyFields")}</th></tr></thead>
              <tbody>{h.legacy.rows.map((d) => <tr key={d.id}><td className="num">{d.number ?? "–"}</td><td className="num">{when(d.documentAt)}</td><td>{d.table}{d.kind ? ` · ${d.kind}` : ""}</td>
                <td className="num ch-fields" dir="ltr">{Object.entries(d.fields).map(([k, v]) => `${k} ${v ?? "–"}`).join(" · ") || "–"}</td></tr>)}</tbody>
            </table></div>
            <Pager p={h.legacy} k="legacyPage" id="ch-legacy-pager" />
            {Object.keys(h.legacy.sums).length > 0 && <p className="hint num ch-fields" dir="ltr" data-testid="ch-legacy-sums">
              <span dir="auto">{t("chLegacySums")}: </span>{Object.entries(h.legacy.sums).map(([k, v]) => `${k} ${v}`).join(" · ")}</p>}</>}
      {anyResidual && <p className="hint" data-testid="ch-residual-hint">{t("chResidualHint")}</p>}
    </div>
  );
}
